/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, MessagesSquare, CheckCircle2 } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import { MESSAGE_CATEGORIES, labelFor, relativeTime } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function FirmInbox() {
    const [threads, setThreads] = useState([]);
    const [clients, setClients] = useState([]);
    const [filterClient, setFilterClient] = useState("all");
    const [filterCategory, setFilterCategory] = useState("all");
    const [filterStatus, setFilterStatus] = useState("open");
    const [selected, setSelected] = useState(null);
    const [thread, setThread] = useState(null);
    const [reply, setReply] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    async function loadThreads() {
        setLoading(true);
        try {
            const params = {};
            if (filterStatus !== "all") params.status = filterStatus;
            if (filterCategory !== "all") params.category = filterCategory;
            if (filterClient !== "all") params.client_id = filterClient;
            const { data } = await api.get("/messages/threads", { params });
            setThreads(data);
            if (data.length && !selected) setSelected(data[0].id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        api.get("/clients/").then((r) => setClients(r.data)).catch(() => {});
    }, []);
    useEffect(() => { loadThreads(); /* eslint-disable-line */ }, [filterCategory, filterStatus, filterClient]);
    useEffect(() => {
        if (!selected) return setThread(null);
        api.get(`/messages/threads/${selected}`).then((r) => setThread(r.data)).catch(() => {});
    }, [selected]);

    async function sendReply() {
        if (!reply.trim()) return;
        setBusy(true);
        try {
            await api.post(`/messages/threads/${selected}/reply`, { body: reply });
            setReply("");
            const { data } = await api.get(`/messages/threads/${selected}`);
            setThread(data);
            await loadThreads();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function resolveThread() {
        if (!selected) return;
        try {
            await api.patch(`/messages/threads/${selected}/status`, null, { params: { status: "resolved" } });
            toast.success("Thread marked resolved");
            await loadThreads();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

    return (
        <div>
            <PageHeader
                eyebrow="Inbox"
                title="Unified client inbox"
                description="Every client conversation in one place — categorised, filtered, and tied to the right entity. Move clients away from scattered emails and texts."
                actions={<NewThreadDialog clients={clients} onCreated={loadThreads} />}
                testId="inbox-header"
            />

            {/* Filters */}
            <div className="co-card co-card-pad flex flex-wrap gap-3 items-end">
                <FilterSelect label="Client" value={filterClient} onChange={setFilterClient} testId="filter-client">
                    <SelectItem value="all">All clients</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </FilterSelect>
                <FilterSelect label="Category" value={filterCategory} onChange={setFilterCategory} testId="filter-category">
                    <SelectItem value="all">All categories</SelectItem>
                    {MESSAGE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </FilterSelect>
                <FilterSelect label="Status" value={filterStatus} onChange={setFilterStatus} testId="filter-status">
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                </FilterSelect>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-[360px_1fr]">
                {/* List */}
                <div className="co-card overflow-hidden">
                    <div className="border-b border-[#E4E4E7] px-4 py-3 flex items-center justify-between">
                        <span className="co-label">Threads</span>
                        <span className="text-xs text-[#5E636E]">{threads.length}</span>
                    </div>
                    <ul className="divide-y divide-[#E4E4E7] max-h-[640px] overflow-y-auto" data-testid="thread-list">
                        {loading && <li className="p-4 text-sm text-[#5E636E]">Loading…</li>}
                        {!loading && threads.length === 0 && (
                            <li className="p-6 text-sm text-[#5E636E]">No threads. Try adjusting filters or creating a new thread.</li>
                        )}
                        {threads.map((t) => (
                            <li key={t.id}>
                                <button
                                    type="button"
                                    onClick={() => setSelected(t.id)}
                                    data-testid={`thread-item-${t.id}`}
                                    className={`w-full text-left p-4 hover:bg-zinc-50 transition-colors ${selected === t.id ? "bg-zinc-50" : ""}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-medium truncate">{t.subject}</span>
                                        <StatusBadge status={t.status} />
                                    </div>
                                    <div className="mt-1 text-xs text-[#5E636E] truncate">
                                        {clientName(t.client_id)} · {labelFor(MESSAGE_CATEGORIES, t.category)}
                                    </div>
                                    <div className="mt-1 text-xs text-[#0F1115] line-clamp-1">{t.last_message_preview}</div>
                                    <div className="mt-1 text-[11px] text-[#5E636E]">{relativeTime(t.last_message_at)}</div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Detail */}
                <div className="co-card flex min-h-[480px] flex-col overflow-hidden">
                    {!thread && <EmptyState icon={MessagesSquare} title="Pick a thread" description="Select a conversation from the left to read and reply." />}
                    {thread && (
                        <>
                            <div className="border-b border-[#E4E4E7] px-5 py-4 flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-xs text-[#5E636E]">{clientName(thread.thread.client_id)}</div>
                                    <div className="font-display text-lg font-medium truncate" data-testid="thread-subject">{thread.thread.subject}</div>
                                    <div className="mt-1 flex items-center gap-2 text-[11px] text-[#5E636E]">
                                        <StatusBadge status={thread.thread.status} />
                                        <span>·</span>
                                        <span>{labelFor(MESSAGE_CATEGORIES, thread.thread.category)}</span>
                                    </div>
                                </div>
                                {thread.thread.status === "open" && (
                                    <Button variant="outline" size="sm" onClick={resolveThread} data-testid="resolve-thread-btn">
                                        <CheckCircle2 className="h-4 w-4" /> Mark resolved
                                    </Button>
                                )}
                            </div>
                            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="thread-messages">
                                {thread.messages.map((m) => (
                                    <div key={m.id} className={`flex ${m.sender_role === "client" ? "justify-start" : "justify-end"}`}>
                                        <div className={`max-w-[80%] rounded-lg border px-3 py-2 ${
                                            m.sender_role === "client"
                                                ? "bg-[#FAFAFA] border-[#E4E4E7]"
                                                : "bg-[#0F1115] text-white border-[#0F1115]"
                                        }`}>
                                            <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">
                                                {m.sender_role.replace("_", " ")} · {relativeTime(m.created_at)}
                                            </div>
                                            <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="border-t border-[#E4E4E7] px-5 py-4">
                                <Textarea
                                    rows={3}
                                    placeholder="Reply to this client…"
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    data-testid="reply-textarea"
                                />
                                <div className="mt-2 flex justify-end">
                                    <Button onClick={sendReply} disabled={busy || !reply.trim()} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="reply-send-btn">
                                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reply"}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function FilterSelect({ label, value, onChange, children, testId }) {
    return (
        <div>
            <Label className="text-[11px] text-[#5E636E]">{label}</Label>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger className="mt-1 min-w-[180px]" data-testid={testId}><SelectValue /></SelectTrigger>
                <SelectContent>{children}</SelectContent>
            </Select>
        </div>
    );
}

function NewThreadDialog({ clients, onCreated }) {
    const [open, setOpen] = useState(false);
    const [client_id, setClient] = useState("");
    const [subject, setSubject] = useState("");
    const [category, setCategory] = useState("general");
    const [initial_body, setBody] = useState("");
    const [busy, setBusy] = useState(false);

    async function submit() {
        if (!client_id || !subject || !initial_body) return;
        setBusy(true);
        try {
            await api.post("/messages/threads", { client_id, subject, category, initial_body });
            toast.success("Thread created");
            setOpen(false);
            setSubject(""); setBody(""); setCategory("general"); setClient("");
            onCreated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="new-thread-btn">
                    <Plus className="h-4 w-4" /> New thread
                </Button>
            </DialogTrigger>
            <DialogContent data-testid="new-thread-dialog">
                <DialogHeader><DialogTitle>Start a thread</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Client</Label>
                        <Select value={client_id} onValueChange={setClient}>
                            <SelectTrigger className="mt-1" data-testid="nt-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                            <SelectContent>
                                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Subject</Label>
                        <Input className="mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="nt-subject" />
                    </div>
                    <div>
                        <Label>Category</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger className="mt-1" data-testid="nt-category"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {MESSAGE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Message</Label>
                        <Textarea rows={4} className="mt-1" value={initial_body} onChange={(e) => setBody(e.target.value)} data-testid="nt-body" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} data-testid="nt-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="nt-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create thread"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
